import React, { useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from "@mui/material";
import { PersonRemove } from "@mui/icons-material";
import { removeTrainingGroupMember } from "./api";
import { getTrainingGroupId } from "./types";
import type { ClubMember, TrainingGroup } from "./types";
import DialogHeader from "./DialogHeader";

interface RemoveGroupMemberDialogProps {
  target: {
    group: TrainingGroup;
    member: ClubMember;
  } | null;
  onClose: () => void;
  onSuccess: () => Promise<void> | void;
  onError: (message: string) => void;
}

const RemoveGroupMemberDialog: React.FC<RemoveGroupMemberDialogProps> = ({
  target,
  onClose,
  onSuccess,
  onError,
}) => {
  const [removing, setRemoving] = useState(false);

  const handleRemove = async () => {
    if (!target) return;
    const trainingGroupId = getTrainingGroupId(target.group);
    if (trainingGroupId == null) return;

    try {
      setRemoving(true);
      await removeTrainingGroupMember(trainingGroupId, target.member.handler_id);
      await onSuccess();
      onClose();
    } catch (err) {
      console.error("Error removing group member:", err);
      onError("Failed to remove member from group. Please try again.");
    } finally {
      setRemoving(false);
    }
  };

  return (
    <Dialog
      open={target != null}
      onClose={() => !removing && onClose()}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogHeader
        title="Remove From Group"
        icon={<PersonRemove color="error" />}
        onClose={onClose}
        closeDisabled={removing}
      />
      <DialogContent dividers>
        <Typography variant="body1">
          Are you sure you want to remove{" "}
          <strong>{target?.member.handler_name}</strong> from{" "}
          <strong>{target?.group.group_name}</strong>?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          They will remain a member of the club but will no longer appear in
          this training group. Existing invoices for this group are unaffected.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={removing}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          startIcon={
            removing ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <PersonRemove />
            )
          }
          onClick={handleRemove}
          disabled={removing}
        >
          {removing ? "Removing…" : "Remove From Group"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RemoveGroupMemberDialog;
